import {
  Component,
  computed,
  effect,
  inject,
  signal,
  Signal,
  TemplateRef,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { BannerStore } from '../../../services/api/banner/banner-store.service';
import { CorporateDesignStore } from '../../../services/api/corporate-design/corporate-design-store.service';
import { FontStore } from '../../../services/api/font/font-store.service';
import { FooterStore } from '../../../services/api/footer/footer-store.service';
import { HeaderStore } from '../../../services/api/header/header-store.service';
import { ImageStore } from '../../../services/api/image/image-store.service';
import { LoadingAnimationComponent } from '../../../components/feedback/loading-animation/loading-animation.component';
import { ButtonComponent } from '../../../components/feedback/button/button.component';
import { CmsTextProviderService } from '../../../services/cms-text-provider.service';
import {
  ICorporateDesign,
  ICorporatePaletteColor,
  MAX_ELEMENT_BORDER_ROUNDING,
  MIN_ELEMENT_BORDER_ROUNDING,
} from '@web/dtos';
import { ColorPickerComponent } from '../../../components/data-input/color-picker/color-picker.component';
import { SimpleOverlayComponent } from '../../../components/data-display/simple-overlay/simple-overlay.component';
import { TemplateStringPipe } from '../../../pipes/template-string/TemplateStringPipe.pipe';
import { CommonModule } from '@angular/common';
import { DialogService } from '../../../services/support/dialog/dialog.service';
import { DialogRef } from '@angular/cdk/dialog';
import { IDialogResponse } from '../../../components/dialogs/dialog';
import { DialogComponent } from '../../../components/dialogs/dialog/dialog.component';

type BorderRoundingMode = 'square' | 'round' | 'custom';

@Component({
  selector: 'aw-corporate-design-page',
  imports: [ButtonComponent, ColorPickerComponent, LoadingAnimationComponent, SimpleOverlayComponent, TemplateStringPipe, CommonModule],
  templateUrl: './corporate-design-page.component.html',
})
export class CorporateDesignPageComponent {
  private readonly bannerStore: BannerStore = inject(BannerStore);
  private readonly corporateDesignStore: CorporateDesignStore =
    inject(CorporateDesignStore);
  private readonly fontStore: FontStore = inject(FontStore);
  private readonly footerStore: FooterStore = inject(FooterStore);
  private readonly headerStore: HeaderStore = inject(HeaderStore);
  protected readonly imageStore: ImageStore = inject(ImageStore);
  protected readonly cmsTextProvider: CmsTextProviderService = inject(
    CmsTextProviderService
  );
  private readonly unsavedChanges: WritableSignal<boolean> = signal(false);
  private initialCorporateDesign: ICorporateDesign | null = null;
  protected readonly paletteEntries: WritableSignal<ICorporatePaletteColor[]> =
  signal([]);
  private initialPaletteEntries: ICorporatePaletteColor[] = [];
  private initialPrimaryColorName: string = '';
  protected readonly primaryColorName: WritableSignal<string> = signal('');
  protected readonly activeColorPickerId: WritableSignal<string | null> =
  signal(null);
  protected readonly roundingMode: WritableSignal<BorderRoundingMode> =
  signal('custom');
  protected readonly customBorderRounding: WritableSignal<number> = signal(0);
  protected readonly minimumBorderRounding: number =
  MIN_ELEMENT_BORDER_ROUNDING;
  protected readonly maximumBorderRounding: number =
  MAX_ELEMENT_BORDER_ROUNDING;
  private draggedColorIndex: number | null = null;
  protected readonly activeConfirmOverlay: WritableSignal<number | null> = signal(null);

  private readonly dialogContent = viewChild.required<TemplateRef<unknown>>('confirmDialog');
  private readonly dialogFooter = viewChild.required<TemplateRef<unknown>>('confirmDialogFooter');
  protected readonly dialogService: DialogService = inject(DialogService);
  protected dialogRef: DialogRef<IDialogResponse<any>, DialogComponent<any>>| null = null;
  private selectedPrimaryIndex: number | null = null;

  protected readonly isLoading: Signal<boolean> = computed(
    (): boolean =>
      this.bannerStore.isLoading() ||
      this.corporateDesignStore.isLoading() ||
      this.fontStore.isLoading() ||
      this.footerStore.isLoading() ||
      this.headerStore.isLoading() ||
      this.imageStore.isLoading()
  );

  constructor() {
    effect((): void => {
      if (
        this.initialCorporateDesign ||
        this.corporateDesignStore.isLoading()
      ) {
        return;
      }

      const corporateDesign: ICorporateDesign =
        this.corporateDesignStore.corporateDesign();
      const sortedPalette: ICorporatePaletteColor[] =
        this.sortPrimaryColorFirst(corporateDesign.colorPalette);
      this.initialCorporateDesign = structuredClone({
        ...corporateDesign,
        colorPalette: sortedPalette,
      });
      this.paletteEntries.set(sortedPalette);
      this.initialPaletteEntries = structuredClone(this.paletteEntries());
      this.initialPrimaryColorName =
        corporateDesign.primaryColorName ||
        this.resolvePrimaryColorName(
          corporateDesign.primaryColor,
          this.paletteEntries()
        );
      this.primaryColorName.set(this.initialPrimaryColorName);
      this.setBorderRoundingState(corporateDesign.buttonBorderRounding);
    });
  }

  public hasUnsavedChanges(): boolean {
    return this.unsavedChanges();
  }

  public setHasUnsavedChanges(hasUnsavedChanges: boolean): void {
    this.unsavedChanges.set(hasUnsavedChanges);
  }

  public async save(): Promise<boolean> {
    if (!this.isPaletteValid()) {
      return false;
    }

    const savedCorporateDesign: ICorporateDesign | null =
      await this.corporateDesignStore.save({
        ...this.corporateDesignStore.corporateDesign(),
        colorPalette: structuredClone(this.paletteEntries()),
        primaryColorName: this.primaryColorName(),
      });
    if (!savedCorporateDesign) {
      return false;
    }

    this.initialCorporateDesign = structuredClone(savedCorporateDesign);
    this.initialPaletteEntries = structuredClone(this.paletteEntries());
    this.initialPrimaryColorName = this.primaryColorName();
    this.setHasUnsavedChanges(false);
    return true;
  }

  public cancel(): void {
    if (this.initialCorporateDesign) {
      this.corporateDesignStore.updateCorporateDesign(
        structuredClone(this.initialCorporateDesign)
      );
      this.setBorderRoundingState(
        this.initialCorporateDesign.buttonBorderRounding
      );
      this.paletteEntries.set(structuredClone(this.initialPaletteEntries));
      this.primaryColorName.set(this.initialPrimaryColorName);
      this.activeColorPickerId.set(null);
    }
    this.setHasUnsavedChanges(false);
  }

  protected selectBorderRounding(mode: BorderRoundingMode): void {
    this.roundingMode.set(mode);
    const rounding: number =
      mode === 'square'
        ? 0
        : mode === 'round'
          ? MAX_ELEMENT_BORDER_ROUNDING
          : this.customBorderRounding();
    this.updateBorderRounding(rounding);
  }

  protected updateCustomBorderRounding(event: Event): void {
    const input: HTMLInputElement = event.target as HTMLInputElement;
    const rounding: number = this.normalizeBorderRounding(Number(input.value));
    input.value = String(rounding);
    this.customBorderRounding.set(rounding);
    this.roundingMode.set('custom');
    this.updateBorderRounding(rounding);
  }

  protected changeCustomBorderRounding(change: number): void {
    const rounding: number = this.normalizeBorderRounding(
      this.customBorderRounding() + change
    );
    this.customBorderRounding.set(rounding);
    this.roundingMode.set('custom');
    this.updateBorderRounding(rounding);
  }

  protected isPaletteValid(): boolean {
    return this.paletteEntries().every(
      (color: ICorporatePaletteColor, index): boolean =>
        /^[0-9a-fA-F]{6}$/.test(color.value) && color.name.trim().length > 0 && this.validColorName(index, color.name)
    );
  }

  protected addColor(): void {
    this.applyPalette([
      ...this.paletteEntries(),
      {
        id: `palette-color-${Date.now()}`,
        value: 'FFFFFF',
        name: '',
        primary: false,
      },
    ]);
  }

  protected updateColorValue(index: number, event: Event): void {
    const input: HTMLInputElement = event.target as HTMLInputElement;
    const value: string = input.value
      .replace(/#/g, '')
      .replace(/[^0-9a-f]/gi, '')
      .slice(0, 6)
      .toUpperCase();
    this.updatePaletteColor(index, { value });
  }

  protected updateColorFromPicker(index: number, value: string): void {
    this.updatePaletteColor(index, {
      value: this.normalizeColor(value).slice(0, 6),
    });
  }

  protected updateColorName(index: number, event: Event): void {
    this.updatePaletteColor(index, {
      name: (event.target as HTMLInputElement).value,
    });
  }

  protected validColorName(index: number, name: string) {
    return this.paletteEntries().findIndex(x => x.name === name) === index;
  }

  protected setPrimaryColor(): void {
    const selectedColor: ICorporatePaletteColor | undefined =
      this.paletteEntries()[this.selectedPrimaryIndex!];
    if (!selectedColor) {
      return;
    }

    this.applyPalette([
      { ...selectedColor, primary: true },
      ...this.paletteEntries()
        .filter(
          (_: ICorporatePaletteColor, colorIndex: number): boolean =>
            colorIndex !== this.selectedPrimaryIndex!
        )
        .map(
          (color: ICorporatePaletteColor): ICorporatePaletteColor => ({
            ...color,
            primary: false,
          })
        ),
    ]);
    this.dialogRef?.close();
    this.dialogRef = null;
    this.selectedPrimaryIndex = null;
  }

  protected deleteColor(index: number): void {
    const colors: ICorporatePaletteColor[] = this.paletteEntries().filter(
      (_: ICorporatePaletteColor, colorIndex: number): boolean =>
        colorIndex !== index
    );
    if (this.paletteEntries()[index]?.primary && colors.length > 0) {
      colors[0] = { ...colors[0], primary: true };
    }
    this.activeColorPickerId.set(null);
    this.applyPalette(colors);
  }

  protected startColorDrag(index: number): void {
    this.draggedColorIndex = index;
  }

  protected dropColor(index: number, event: DragEvent): void {
    event.preventDefault();
    if (this.draggedColorIndex === null || this.draggedColorIndex === index) {
      this.draggedColorIndex = null;
      return;
    }
    const colors: ICorporatePaletteColor[] = [...this.paletteEntries()];
    const [draggedColor] = colors.splice(this.draggedColorIndex, 1);
    colors.splice(index, 0, draggedColor);
    this.draggedColorIndex = null;
    this.applyPalette(colors);
  }

  protected swatchColor(value: string): string {
    return /^[0-9a-fA-F]{6}$/.test(value) ? `#${value}` : 'transparent';
  }

  protected confirmNewPrimary(index: number){
    this.selectedPrimaryIndex = index;
    this.dialogRef = this.dialogService.openGenericDialog({
      headline: this.cmsTextProvider.corporate.changePrimaryColor,
      width: '25vw',
      content: this.dialogContent(),
      footer: this.dialogFooter()
    });
  }

  private updateBorderRounding(buttonBorderRounding: number): void {
    this.corporateDesignStore.updateCorporateDesign({
      ...this.corporateDesignStore.corporateDesign(),
      buttonBorderRounding,
    });
    this.setHasUnsavedChanges(true);
  }

  private setBorderRoundingState(rounding: number): void {
    this.customBorderRounding.set(this.normalizeBorderRounding(rounding));
    this.roundingMode.set(
      rounding === 0
        ? 'square'
        : rounding === MAX_ELEMENT_BORDER_ROUNDING
          ? 'round'
          : 'custom'
    );
  }

  private applyPalette(colors: ICorporatePaletteColor[]): void {
    const sortedColors: ICorporatePaletteColor[] =
      this.sortPrimaryColorFirst(colors);
    const updatedDesign: ICorporateDesign = {
      ...this.corporateDesignStore.corporateDesign(),
    };

    const primaryColor: ICorporatePaletteColor | undefined = sortedColors.find(
      (color: ICorporatePaletteColor): boolean => color.primary
    );
    if (primaryColor && /^[0-9a-fA-F]{6}$/.test(primaryColor.value)) {
      Object.assign(
        updatedDesign,
        this.applyPrimaryColorToStandardControls(
          updatedDesign,
          primaryColor.value
        )
      );
      this.primaryColorName.set(primaryColor.name);
    } else if (!primaryColor) {
      this.primaryColorName.set(
        `#${this.normalizeColor(updatedDesign.primaryColor)}`
      );
    }

    this.paletteEntries.set(structuredClone(sortedColors));
    this.corporateDesignStore.updateCorporateDesign({
      ...updatedDesign,
      colorPalette: structuredClone(sortedColors),
      primaryColorName: this.primaryColorName(),
    });
    this.setHasUnsavedChanges(true);
  }

  private updatePaletteColor(
    index: number,
    changes: Partial<ICorporatePaletteColor>
  ): void {
    this.applyPalette(
      this.paletteEntries().map(
        (
          color: ICorporatePaletteColor,
          colorIndex: number
        ): ICorporatePaletteColor =>
          colorIndex === index ? { ...color, ...changes } : color
      )
    );
  }

  private sortPrimaryColorFirst(
    colors: ICorporatePaletteColor[]
  ): ICorporatePaletteColor[] {
    return [...colors]
      .sort(
        (
          firstColor: ICorporatePaletteColor,
          secondColor: ICorporatePaletteColor
        ): number => Number(secondColor.primary) - Number(firstColor.primary)
      )
      .map(
        (color: ICorporatePaletteColor): ICorporatePaletteColor => ({
          ...color,
        })
      );
  }

  private resolvePrimaryColorName(
    primaryColor: string,
    colors: ICorporatePaletteColor[]
  ): string {
    const normalizedPrimaryColor: string = this.normalizeColor(primaryColor);
    return (
      colors.find(
        (color: ICorporatePaletteColor): boolean =>
          this.normalizeColor(color.value) === normalizedPrimaryColor
      )?.name ?? this.cmsTextProvider.corporate.custom
    );
  }

  private normalizeColor(color: string): string {
    return color.replace(/#/g, '').toUpperCase();
  }

  private normalizeBorderRounding(value: number): number {
    const roundedValue: number = Number.isFinite(value) ? Math.round(value) : 0;
    return Math.min(
      MAX_ELEMENT_BORDER_ROUNDING,
      Math.max(MIN_ELEMENT_BORDER_ROUNDING, roundedValue)
    );
  }

  private applyPrimaryColorToStandardControls(
    design: ICorporateDesign,
    color: string
  ): ICorporateDesign {
    const normalizedColor: string = this.normalizeColor(color);
    const primaryColor = `#${normalizedColor}`;
    const contrastColor: string =
      this.getReadableContrastColor(normalizedColor);

    return {
      ...design,
      primaryColor,
      primaryFontColor: contrastColor,
      primaryBorderColor: primaryColor,
      primaryAlternativeColor: primaryColor,
      primaryDarkColor: primaryColor,
      primaryDarkFontColor: contrastColor,
      primaryDarkBorderColor: primaryColor,
      primaryDarkAlternativeColor: primaryColor,
    };
  }

  private getReadableContrastColor(color: string): string {
    const channels: number[] = [0, 2, 4].map((index: number): number => {
      const channel: number =
        Number.parseInt(color.slice(index, index + 2), 16) / 255;
      return channel <= 0.03928
        ? channel / 12.92
        : Math.pow((channel + 0.055) / 1.055, 2.4);
    });
    const luminance: number =
      0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
    return luminance > 0.179 ? '#000000' : '#FFFFFF';
  }
}
